// src/App.js
import React, { Suspense, lazy, useEffect, useRef, useContext } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { WalletProvider, WalletContext } from './components/WalletConnect';
import Navbar from './components/Navbar';
import VipClub from './components/VipClub';
import { ToastContainer } from 'react-toastify';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Button,
  Typography,
  CircularProgress,
  LinearProgress,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import 'react-toastify/dist/ReactToastify.css';
import theme, { applyThemeCssVars } from './theme';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import Seo from './components/Seo';
import GlobalChat from './components/Chat';
import ChatBots from './components/ChatBots';
import SettingsFab from './components/SettingsFab';
import MascotWidget from './components/MascotWidget';
import GameErrorBoundary from './games/shared/GameErrorBoundary';
import InstallPrompt from './components/InstallPrompt';

/* ---------------- SSR-safe helpers ---------------- */
const safeWindow = () => { try { return typeof window !== 'undefined' ? window : null; } catch { return null; } };
const safeDocument = () => { try { return typeof document !== 'undefined' ? document : null; } catch { return null; } };
const prefersReducedMotion = () => {
  try { const w = safeWindow(); return !!(w && w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch { return false; }
};

/* --------------- Create a MUI theme bridged to our tokens & FX vars --------------- */
const muiTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: theme.colors.primary },
    secondary: { main: theme.colors.secondary || theme.colors.accent },
    background: {
      default: theme.colors.background,
      paper: theme.colors.surface || theme.colors.card,
    },
    text: { primary: theme.colors.text, secondary: theme.colors.subText },
    divider: theme.colors.border,
  },
  shape: { borderRadius: parseInt(theme.radii?.card, 10) || 6 },
  typography: {
    fontFamily:
      'var(--font-sans, Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Helvetica Neue, Arial, "Apple Color Emoji", "Segoe UI Emoji")',
    h1: theme.typography?.h1 || { fontWeight: 900, letterSpacing: 0.4, fontSize: 'clamp(36px, 6.5vw, 72px)', lineHeight: 1.08 },
    h2: theme.typography?.h2 || { fontWeight: 800, letterSpacing: 0.2, fontSize: 'clamp(28px, 4.5vw, 48px)', lineHeight: 1.15 },
    h3: theme.typography?.h3 || { fontWeight: 800, letterSpacing: 0.2, fontSize: 'clamp(22px, 3.2vw, 36px)', lineHeight: 1.18 },
    body1: theme.typography?.body1 || { fontSize: '1rem', lineHeight: 1.6 },
  },

  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          position: 'relative',
          border: 'none',
          height: 'var(--fx-divider-thin-height)',
          opacity: 'var(--fx-divider-rainbow-thin-opacity)',
          background: 'var(--asset-divider-rainbow-thin) center / cover no-repeat',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 'var(--radius-button)',
          textTransform: 'none',
          fontWeight: 900,
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'var(--fx-button-glint-url) center / cover no-repeat',
            opacity: 'var(--fx-button-glint-opacity)',
            transition: 'opacity .18s ease',
            pointerEvents: 'none',
            borderRadius: 'inherit',
          },
          '&:hover::after, &.Mui-focusVisible::after': {
            opacity: 'var(--fx-button-glint-hover)',
          },
          '&.Mui-disabled::after': { opacity: 0 },
        },
        contained: {
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '-2px',
            background: 'var(--fx-button-pulse-url) center / cover no-repeat',
            opacity: 'var(--fx-button-pulse-opacity)',
            transform: 'scale(0.98)',
            transition: 'opacity .25s ease, transform .25s ease',
            pointerEvents: 'none',
            zIndex: -1,
            borderRadius: 'inherit',
          },
          '&:hover::before, &.Mui-focusVisible::before': {
            opacity: 'calc(var(--fx-button-pulse-opacity) + .15)',
            transform: 'scale(1)',
          },
          '&.Mui-disabled::before': { opacity: 0 },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 'var(--radius-card)',
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '-1px',
            background: 'var(--fx-card-glow-url) center / cover no-repeat',
            opacity: 'var(--fx-card-glow-opacity)',
            mixBlendMode: 'var(--fx-blend-mode)',
            pointerEvents: 'none',
            borderRadius: 'inherit',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 'var(--radius-card)',
          background: 'var(--color-card)',
          border: '1px solid var(--color-border)',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: '-1px',
            background: 'var(--fx-card-glow-url) center / cover no-repeat',
            opacity: 'var(--fx-card-glow-opacity)',
            mixBlendMode: 'var(--fx-blend-mode)',
            pointerEvents: 'none',
            borderRadius: 'inherit',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: 'var(--color-card)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-card)',
        },
        arrow: { color: 'var(--color-card)' },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 'var(--radius-card)', background: 'var(--color-card)', border: '1px solid var(--color-border)' } },
    },
    MuiMenu: {
      styleOverrides: { paper: { borderRadius: 'var(--radius-card)', background: 'var(--color-card)', border: '1px solid var(--color-border)' } },
    },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: 'var(--radius-card)', background: 'var(--color-card)', border: '1px solid var(--color-border)' } },
    },
    MuiBackdrop: {
      styleOverrides: { root: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,.65) !important' } },
    },
  },
});

/* ---------------- Lazy pages ---------------- */
const Home = lazy(() => import('./pages/Home'));
const Games = lazy(() => import('./pages/Games'));
const Token = lazy(() => import('./pages/Token'));
const NFT = lazy(() => import('./pages/NFT'));
const Staking = lazy(() => import('./pages/Staking'));
const DAO = lazy(() => import('./pages/DAO'));
const Admin = lazy(() => import('./pages/Admin'));
const PromotionsPage = lazy(() => import('./pages/Promotions'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const JoinPage = lazy(() => import('./pages/Join'));
const FAQ = lazy(() => import('./pages/FAQ'));
const Contact = lazy(() => import('./pages/Contact'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const ResponsiblePlay = lazy(() => import('./pages/ResponsiblePlay'));
const ProvablyFair = lazy(() => import('./pages/ProvablyFair'));
const About = lazy(() => import('./pages/About'));
// Sports and Prediction removed — re-add when features are live
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Footer = lazy(() => import('./components/Footer'));

/* ---------------- Lazy games ---------------- */
const HoneyPot = lazy(() => import('./games/HoneyPot'));
const BossFight = lazy(() => import('./games/BossFight'));
const TicketSnipe = lazy(() => import('./games/TicketSnipe'));
const Coinflip = lazy(() => import('./games/CoinFlip'));
const Dice = lazy(() => import('./games/Dice'));
const Plinko = lazy(() => import('./games/Plinko'));
const Roulette = lazy(() => import('./games/Roulette'));
const Keno = lazy(() => import('./games/Keno'));
const Slots = lazy(() => import('./games/Slots'));
const Crash = lazy(() => import('./games/Crash'));
const Mines = lazy(() => import('./games/Mines'));
const Coins = lazy(() => import('./games/Coins'));
const HiLo = lazy(() => import('./games/HiLo'));

/* ---------------- Scroll to hash anchors ---------------- */
const ScrollToHash = () => {
  const loc = useLocation();
  useEffect(() => {
    const d = safeDocument(); const w = safeWindow(); if (!w || !d) return;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    if (loc.hash) {
      const el = d.getElementById(loc.hash.slice(1));
      if (el) try { el.scrollIntoView({ behavior, block: 'start' }); } catch { el.scrollIntoView(true); }
    } else {
      try { w.scrollTo({ top: 0, behavior }); } catch { w.scrollTo(0, 0); }
    }
  }, [loc]);
  return null;
};

/* ---------------- Decorative floating plasma ---------------- */
const PlasmaBackground = () => {
  const dense = useMediaQuery('(min-width:600px)');
  const preferReduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const N = preferReduced ? 0 : (dense ? 16 : 9);
  const seeds = React.useMemo(() => (
    Array.from({ length: N }).map(() => ({
      size: Math.random() * 6 + 4,
      top: Math.random() * 100,
      left: Math.random() * 100,
      delay: Math.random() * 4,
      duration: Math.random() * 8 + 6,
      alt: Math.random() > 0.5,
    }))
  ), [N]);
  return (
    <Box sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', opacity: 0.10 }}>
      {seeds.map((s, i) => {
        const style = {
          position: 'absolute', width: s.size, height: s.size, borderRadius: '50%',
          background: s.alt ? muiTheme.palette.secondary.main : muiTheme.palette.primary.main,
          top: `${s.top}%`, left: `${s.left}%`,
        };
        return preferReduced ? (
          <Box key={i} sx={style} />
        ) : (
          <motion.div key={i} style={style} animate={{ y: ['0%', '-120%'] }} transition={{ duration: s.duration, repeat: Infinity, ease: 'linear', delay: s.delay }} />
        );
      })}
    </Box>
  );
};

/* ---------------- Error Boundary ---------------- */
class AppErrorBoundary extends React.Component {
  constructor(props){ super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error){ return { hasError: true, error }; }
  componentDidCatch(error, info){ try { console.error('AppErrorBoundary', error, info); } catch {} }
  render(){
    if (this.state.hasError) return (
      <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, textAlign: 'center', color: 'text.secondary' }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Something went wrong</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>Please try reloading the page.</Typography>
        <Button variant="contained" onClick={()=> { try { safeWindow()?.location?.reload?.(); } catch {} }} sx={{ borderRadius: 1.5 }}>Reload</Button>
      </Box>
    );
    return this.props.children;
  }
}

/* ---------------- VIP inline page ---------------- */
const vipLevels = [
  { level: 'Shrimp',  icon: '🦐', color: theme.vip.palette.shrimp,  need: 0,   rake: 0,  perks: [{ text: 'Weekly Free Spins', icon: '❤️' }, { text: 'Standard Withdrawals', icon: '🎉' }] },
  { level: 'Fish',    icon: '🐟', color: theme.vip.palette.fish,    need: 20,  rake: 2,  perks: [{ text: '2% Rakeback', icon: '💰' }, { text: 'Priority Support', icon: '⚡' }] },
  { level: 'Dolphin', icon: '🐬', color: theme.vip.palette.dolphin, need: 50,  rake: 5,  perks: [{ text: '5% Rakeback', icon: '💰' }, { text: 'Personal VIP Manager', icon: '💎' }, { text: 'Higher Bet Limits', icon: '📈' }] },
  { level: 'Whale',   icon: '🐋', color: theme.vip.palette.whale,   need: 100, rake: 10, perks: [{ text: '10% Rakeback', icon: '💰' }, { text: 'Elite VIP Events', icon: '⭐' }, { text: 'Premium Rewards', icon: '🏆' }] },
];
const VipPage = () => <VipClub theme={theme} vipLevels={vipLevels} />;

// Layout constants for desktop sidebars
const CHAT_WIDTH = 360;  // right chat dock width

/* ---------------- Main App ---------------- */
function App() {
  const location = useLocation();
  const reduceMotion = prefersReducedMotion();
  const fade = { duration: reduceMotion ? 0 : 0.6 };

  const mainRef = useRef(null);
  useEffect(() => { try { mainRef.current?.focus?.(); } catch {} }, [location.pathname]);
  const isXs = useMediaQuery('(max-width:600px)');
  const hideFooter = /^\/games\//.test(location.pathname) && isXs;

  // Build-time stamp for og:updated_time (optional)
  let buildTime;
  try {
    buildTime = process.env.REACT_APP_BUILD_TIME || (import.meta && import.meta.env && import.meta.env.VITE_BUILD_TIME);
  } catch {}
  const DEFAULT_OG_IMAGE = '/bluedice1200.png';

  // If an admin share param is present, force noindex
  const hasAdminParam = (() => { try { return new URLSearchParams(location.search).has('admin'); } catch { return false; } })();

  /* ---------------- Active chain label for SEO & copy ---------------- */
  const walletCtx = useContext(WalletContext) || {};
  const activeChainId = Number(
    walletCtx.chainId ||
    (typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem('cp:lastChainId') : null) ||
    process.env.REACT_APP_CHAIN_ID_8453 ||
    8453
  );
  const CHAIN_NAMES = { 1: 'Ethereum', 10: 'Optimism', 137: 'Polygon', 42161: 'Arbitrum', 43114: 'Avalanche', 8453: 'Base', 484: 'CAMP' };
  const chainLabel = walletCtx.networkName || CHAIN_NAMES[activeChainId] || 'Base';

  // Route-specific SEO overrides
  const routeMeta = React.useMemo(() => {
    const p = location.pathname;
    const baseDesc = `Play provably‑fair games on ${chainLabel} with instant payouts, on‑chain jackpots, and VIP rakeback.`;
    if (p === '/') {
      return {
        title: 'ChainPlay – The #1 Blockchain Casino',
        description: baseDesc + ' Crash, Dice, Coinflip, Hi‑Lo, Plinko, Mines, Keno & more.',
      };
    }
    if (p.startsWith('/games/')) {
      const seg = p.split('/')[2] || '';
      const gameSeo = {
        crash:      { name: 'Crash',      desc: 'Set a target multiplier and cash out before the crash. Higher risk = higher reward. Provably fair on Base.' },
        dice:       { name: 'Dice',       desc: 'Pick a number 1–99 and roll under to win. Lower targets mean bigger payouts. On-chain verified.' },
        coinflip:   { name: 'Coinflip',   desc: 'Heads or tails — 50/50 odds, 2x payout. The simplest provably fair bet on Base.' },
        mines:      { name: 'Mines',      desc: 'Navigate a 5×5 grid avoiding hidden mines. Reveal more tiles for higher multipliers.' },
        plinko:     { name: 'Plinko',     desc: 'Drop a ball through pegs and watch it bounce to your payout. Choose your risk level.' },
        slots:      { name: 'Slots',      desc: 'Spin 5 reels for payouts up to 5000×. Three difficulty tiers with on-chain settlement.' },
        roulette:   { name: 'Roulette',   desc: 'Bet on a number (35×) or color (2×). Classic wheel game with provably fair spins.' },
        hilo:       { name: 'Hi-Lo',      desc: 'Predict whether the next card is higher or lower. Streak your way to bigger multipliers.' },
        keno:       { name: 'Keno',       desc: 'Pick up to 20 numbers from 40. The more you match, the bigger the payout.' },
        coins:      { name: 'Coins',      desc: 'Predict multiple coin flips. More coins = higher risk and reward. Binomial probability game.' },
        honeypot:   { name: 'Honey Pot',  desc: 'Jackpot game — be the last contributor when the timer expires to win the entire pot.' },
        bossfight:  { name: 'Boss Fight', desc: 'Raid boss battle — deal damage with ETH. Kill the boss for the killer share, or earn team rewards.' },
        ticketsnipe:{ name: 'Ticket Snipe',desc: 'Dynamic fuse timer with ticket raffle endgame. Last ticket holder wins.' },
      };
      const g = gameSeo[seg] || { name: seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), desc: `Play with provably‑fair rounds on ${chainLabel}.` };
      return {
        title: `${g.name} – ChainPlay on ${chainLabel}`,
        description: g.desc,
      };
    }
    switch (p) {
      case '/games':
        return { title: `Games – ChainPlay on ${chainLabel}`, description: 'Browse Crash, Dice, Coinflip, Hi‑Lo, Plinko, Mines, Keno & more.' };
      // Sports and Prediction routes removed — re-add when features are live
      case '/join':
        return { title: 'Join ChainPlay Beta – Claim CPT', description: 'Connect your wallet and link X/Twitter to join the ChainPlay beta on ' + chainLabel + '.' };
      case '/token':
        return { title: 'CPT Token – ChainPlay', description: 'Explore CPT token details, staking and treasury addresses.' };
      case '/staking':
        return { title: 'Staking – ChainPlay', description: `Stake CPT on ${chainLabel} and earn rewards with transparent on‑chain payouts.` };
      case '/nft':
        return { title: 'Boost NFTs – ChainPlay', description: `Mint Boost NFTs on ${chainLabel} to enhance your gameplay and rewards.` };
      case '/promotions':
        return { title: 'Promotions – ChainPlay', description: 'Exclusive bonuses and promotions for ChainPlay members.' };
      case '/faq':
        return { title: 'FAQ – ChainPlay', description: 'Answers to common questions about playing on ChainPlay.' };
      case '/contact':
        return { title: 'Contact – ChainPlay', description: 'Get in touch with the ChainPlay team.' };
      case '/terms':
        return { title: 'Terms of Service – ChainPlay', description: 'Read ChainPlay’s Terms of Service.' };
      case '/privacy':
        return { title: 'Privacy Policy – ChainPlay', description: 'Learn how ChainPlay handles your data.' };
      case '/responsible-play':
        return { title: 'Responsible Play – ChainPlay', description: 'Guidelines and tools to help you play responsibly.' };
      case '/provably-fair':
        return { title: 'Provably Fair – ChainPlay', description: 'How ChainPlay ensures fairness using on‑chain commits and reveals.' };
      case '/about':
        return { title: 'About – ChainPlay', description: 'ChainPlay’s mission and the team behind the app.' };
      case '/admin':
        return { title: 'Admin – ChainPlay', description: 'Administrative console', robots: 'noindex,nofollow' };
      case '/dashboard':
        return { title: 'Dashboard – ChainPlay', description: 'Your profile and account overview.' };
      case '/vip':
        return { title: 'VIP Club – ChainPlay', description: 'VIP levels, rakeback, and exclusive rewards.' };
      case '/404':
        return { title: 'Page Not Found – ChainPlay', description: 'Sorry, that page does not exist. Browse games or return home.', robots: 'noindex,nofollow' };
      default:
        return {};
    }
  }, [location.pathname, chainLabel]);

  const seoProps = React.useMemo(() => {
    const robots = hasAdminParam ? 'noindex,nofollow' : (routeMeta.robots || 'index,follow');
    return {
      ...routeMeta,
      robots,
      image: routeMeta.image || DEFAULT_OG_IMAGE,
      modifiedTime: buildTime,
    };
  }, [routeMeta, hasAdminParam, DEFAULT_OG_IMAGE, buildTime]);

  // Re-apply CSS vars in case runtime theme swaps occur
  useEffect(() => { try { applyThemeCssVars(); } catch {} }, []);

  const inHoneyPot = location.pathname.startsWith('/games/honeypot');
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Show a slim top progress bar briefly on route changes (respects reduced motion)
  const [routeLoading, setRouteLoading] = React.useState(false);
  useEffect(() => {
    if (reduceMotion) { setRouteLoading(false); return; }
    setRouteLoading(true);
    const t = setTimeout(() => setRouteLoading(false), 900);
    return () => clearTimeout(t);
  }, [location.pathname, reduceMotion]);

  // Right chat dock expanded/collapsed (desktop)
  const [chatExpanded, setChatExpanded] = React.useState(() => {
    try {
      const v = localStorage.getItem('cp:chatDock');
      if (v === '0') return false;
      if (v === '1') return true;
    } catch {}
    return false; // default closed on desktop
  });
  useEffect(() => {
    try {
      localStorage.setItem('cp:chatDock', chatExpanded ? '1' : '0');
      const de = document.documentElement;
      de.style.setProperty('--chat-right-width', chatExpanded ? `${CHAT_WIDTH}px` : '0px');
    } catch {}
  }, [chatExpanded]);
  useEffect(() => {
    try {
      const onToggle = () => setChatExpanded((v) => !v);
      const onExpand = () => setChatExpanded(true);
      const onCollapse = () => setChatExpanded(false);
      window.addEventListener('toggle_chat_dock', onToggle);
      window.addEventListener('expand_chat_dock', onExpand);
      window.addEventListener('collapse_chat_dock', onCollapse);

      const onKey = (e) => {
        const mod = e.ctrlKey || e.metaKey;
        if (mod && (e.key === 'j' || e.key === 'J')) {
          e.preventDefault();
          onToggle();
        }
      };
      window.addEventListener('keydown', onKey);

      return () => {
        window.removeEventListener('toggle_chat_dock', onToggle);
        window.removeEventListener('expand_chat_dock', onExpand);
        window.removeEventListener('collapse_chat_dock', onCollapse);
        window.removeEventListener('keydown', onKey);
      };
    } catch {}
  }, []);

  // Page transition variants (gentle; respects fade duration above)
  const pageVariants = React.useMemo(() => ({
    initial: { opacity: 0, y: 8, filter: 'saturate(0.95)' },
    enter:   { opacity: 1, y: 0, filter: 'saturate(1)', transition: { duration: fade.duration, ease: 'easeOut' } },
    exit:    { opacity: 0, y: -8, filter: 'saturate(0.95)', transition: { duration: Math.max(0.12, fade.duration * 0.8), ease: 'easeIn' } },
  }), [fade.duration]);

  // Announce route changes for screen readers (pairs with #sr-updates)
  useEffect(() => {
    try { window.srAnnounce?.(routeMeta?.title || document?.title || 'Navigation'); } catch {}
  }, [location.pathname, routeMeta?.title]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <WalletProvider autoConnect targetChainId={Number(process.env.REACT_APP_CHAIN_ID || process.env.VITE_CHAIN_ID || 8453)}>

        {/* a11y skip link */}
        <Box
          component="a"
          href="#main"
          sx={{
            position: 'absolute', left: -10000, top: 'auto', width: 1, height: 1, overflow: 'hidden',
            '&:focus': {
              position: 'fixed', left: 8, top: 8, zIndex: 2000, width: 'auto', height: 'auto',
              p: 1, bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', borderRadius: 1,
            },
          }}
        >
          Skip to content
        </Box>

        <Navbar />

        {/* Right chat dock (visible on lg and up) */}
        {chatExpanded && (
          <Box
            role="complementary"
            aria-label="Live chat"
            sx={{
              display: { xs: 'none', lg: 'block' },
              position: 'fixed',
              top: 'var(--topbar-height, 56px)',
              right: 0,
              height: 'calc(100% - var(--topbar-height, 56px))',
              width: 'var(--chat-right-width, 0px)',
              minWidth: `${CHAT_WIDTH}px`,
              borderLeft: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              zIndex: 1200,
              overflow: 'hidden',
            }}
          >
            {/* Collapse handle */}
            <Box sx={{ position: 'absolute', top: 6, left: 6, zIndex: 2 }}>
              <Tooltip title="Hide chat">
                <IconButton
                  size="small"
                  onClick={() => { try { window.dispatchEvent(new Event('collapse_chat_dock')); } catch {} }}
                  sx={{ color: 'var(--color-text)', bgcolor: 'rgba(255,255,255,0.06)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' } }}
                  aria-label="Hide chat"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Fill the dock with chat */}
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                <GlobalChat room="global" docked style={{ width: '100%', height: '100%' }} />
              </Box>
            </Box>
          </Box>
        )}

        {/* Small bottom-right Degen Chat button (desktop) */}
        {!chatExpanded && (
          <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              startIcon={<ChatBubbleOutlineIcon />}
              onClick={() => { try { window.dispatchEvent(new Event('expand_chat_dock')); } catch {} }}
              sx={{
                position: 'fixed',
                right: 16,
                bottom: 16,
                zIndex: 1300,
                borderRadius: 9999,
                boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                fontWeight: 800,
                letterSpacing: 0.4,
              }}
              aria-label="Open degen chat"
            >
              Degen Chat
            </Button>
          </Box>
        )}

        {/* Route change progress */}
        {routeLoading && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000 }}>
            <LinearProgress color="secondary" sx={{ height: 2 }} />
          </Box>
        )}

        {/* Global ARIA live region (use window.srAnnounce('...') to speak updates) */}
        <Box
          id="sr-updates"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: 'absolute !important',
            width: 1, height: 1, p: 0, m: '-1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}
        />

        <ScrollToHash />

        <AnimatePresence mode="wait">
          <motion.div key={location.pathname} variants={pageVariants} initial="initial" animate="enter" exit="exit">
            <Box
              id="main"
              ref={mainRef}
              tabIndex={-1}
              role="main"
              className={inHoneyPot ? 'no-global-glint' : undefined}
              sx={{
                minHeight: 'calc(var(--vh, 1vh) * 100)',
                position: 'relative',
                bgcolor: 'background.default',
                mt: 'var(--topbar-height, 56px)',
                ml: { md: 'var(--nav-left-width, 280px)' },
                mr: { lg: 'var(--chat-right-width, 0px)' },
              }}
            >
              <PlasmaBackground />

              <Suspense
                fallback={
                  <Box
                    role="status"
                    aria-busy
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexDirection: 'column', gap: 2, minHeight: '50vh', color: 'secondary.main',
                    }}
                  >
                    <CircularProgress color="secondary" thickness={4} />
                    <Typography sx={{ fontWeight: 800, letterSpacing: 0.3 }}>Loading…</Typography>
                  </Box>
                }
              >
                <AppErrorBoundary>
                  <Routes location={location}>
                    <Route path="/" element={<Home />} />
                    <Route path="/games" element={<Games />} />
                    <Route path="/sports" element={<Navigate to="/games" replace />} />
                    <Route path="/markets" element={<Navigate to="/games" replace />} />
                    <Route path="/games/honeypot" element={<GameErrorBoundary><HoneyPot /></GameErrorBoundary>} />
                    <Route path="/games/bossfight" element={<GameErrorBoundary><BossFight /></GameErrorBoundary>} />
                    <Route path="/games/ticketsnipe" element={<GameErrorBoundary><TicketSnipe /></GameErrorBoundary>} />
                    <Route path="/games/crash" element={<GameErrorBoundary><Crash /></GameErrorBoundary>} />
                    <Route path="/games/mines" element={<GameErrorBoundary><Mines /></GameErrorBoundary>} />
                    <Route path="/games/coinflip" element={<GameErrorBoundary><Coinflip /></GameErrorBoundary>} />
                    <Route path="/games/coins" element={<GameErrorBoundary><Coins /></GameErrorBoundary>} />
                    <Route path="/games/dice" element={<GameErrorBoundary><Dice /></GameErrorBoundary>} />
                    <Route path="/games/hilo" element={<GameErrorBoundary><HiLo /></GameErrorBoundary>} />
                    <Route path="/games/plinko" element={<GameErrorBoundary><Plinko /></GameErrorBoundary>} />
                    <Route path="/games/roulette" element={<GameErrorBoundary><Roulette /></GameErrorBoundary>} />
                    <Route path="/games/keno" element={<GameErrorBoundary><Keno /></GameErrorBoundary>} />
                    <Route path="/games/slots" element={<GameErrorBoundary><Slots /></GameErrorBoundary>} />
                    <Route path="/token" element={<Token />} />
                    <Route path="/nft" element={<NFT />} />
                    <Route path="/staking" element={<Staking />} />
                    <Route path="/dao" element={<DAO />} />
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/promotions" element={<PromotionsPage />} />
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/join" element={<JoinPage />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/responsible-play" element={<ResponsiblePlay />} />
                    <Route path="/provably-fair" element={<ProvablyFair />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/vip" element={<VipPage />} />
                    <Route path="/404" element={<NotFound />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </AppErrorBoundary>
              </Suspense>

              {/* Theme-driven plasma divider above footer (inside main to avoid overlap) */}
              {!hideFooter && <div className="divider-plasma" aria-hidden="true" />}

              {/* Footer (inside main so it respects left/right offsets) */}
              {!hideFooter && (
                <Suspense fallback={null}>
                  <Footer />
                </Suspense>
              )}
            </Box>
          </motion.div>
        </AnimatePresence>

        {/* Global Chat overlay for small/medium screens */}
        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
          <GlobalChat room="global" />
        </Box>

        {/* Background liveliness bot (transparent demo chatter; safe & labeled) */}
        {!isAdminRoute && (
          <ChatBots
            room="global"
            enabled
            personaPack="glam"
            minIntervalMs={15000}
            maxIntervalMs={30000}
            quietMs={12000}
          />
        )}

        {/* Render SEO last so it overrides any page-level <Seo> */}
        <Seo key={location.pathname} path={location.pathname} {...seoProps} />

        <ToastContainer
          position="bottom-right"
          theme="dark"
          newestOnTop
          limit={3}
          autoClose={2400}
          pauseOnFocusLoss
          closeOnClick
          draggable={false}
          hideProgressBar={false}
          closeButton={false}
        />

        <MascotWidget />
        <SettingsFab />
        <InstallPrompt />
      </WalletProvider>
    </ThemeProvider>
  );
}

export default App;
